import * as React from "react";
import { splitEscaped } from "./split";

// Forest plot: one row per run, a point (the pass rate) and a bar (its
// interval) on a shared 0-100% axis, with an optional reference line and a
// shaded band around it (e.g. the smallest gap the sample could detect).
//
// Props match the MDX registry entry:
// `labels` comma-separated run names (write "\," for a literal comma)
// `rates`/`lows`/`highs` percent values 0..100, one per run
// `kinds`  optional, comma-separated mark per run: solid | outline | hatch |
//          dotted. Marks differ by fill and texture, not colour. Missing or
//          unknown entries cycle through the four in that order.
// `base`   optional reference rate in percent (drawn as a vertical rule)
// `band`   optional half-width in points around `base` (needs `base`)
//
// A row without a finite rate shows "no data"; a missing or inverted interval
// is drawn as the point alone and spoken as "interval unknown".

export interface IntervalPlotProps {
  labels: string;
  rates: number[];
  lows: number[];
  highs: number[];
  kinds?: string;
  base?: number;
  band?: number;
}

const KINDS = ["solid", "outline", "hatch", "dotted"] as const;
type Kind = (typeof KINDS)[number];

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const clamp = (n: number) => Math.min(Math.max(n, 0), 100);
const fmt = (n: number) => Number(n.toFixed(1)).toString();

export function IntervalPlot({ labels, rates, lows, highs, kinds, base, band }: IntervalPlotProps) {
  const labelId = React.useId();
  const names = (labels ?? "") === "" ? [] : splitEscaped(labels ?? "", ",").map((s) => s.trim());
  const kindList = (kinds ?? "") === "" ? [] : splitEscaped(kinds ?? "", ",").map((s) => s.trim());
  const count = Math.max(names.length, rates?.length ?? 0);
  const hasBase = isNum(base);
  const hasBand = hasBase && isNum(band) && band > 0;

  const rows = Array.from({ length: count }, (_, i) => {
    const name = i < names.length ? names[i] || "—" : "(unnamed)";
    const kind: Kind = (KINDS as readonly string[]).includes(kindList[i] ?? "")
      ? (kindList[i] as Kind)
      : KINDS[i % KINDS.length]!;
    const rate = rates?.[i];
    const lo = lows?.[i];
    const hi = highs?.[i];
    const hasRate = isNum(rate);
    const hasRange = hasRate && isNum(lo) && isNum(hi) && lo <= hi;
    return { name, kind, rate: hasRate ? rate : null, lo: hasRange ? lo : null, hi: hasRange ? hi : null };
  });

  const bandLo = hasBand ? clamp(base! - band!) : 0;
  const bandHi = hasBand ? clamp(base! + band!) : 0;

  return (
    <div className="gc-widget iv-widget">
      <div className="gc-widget-label" id={labelId}>
        <span aria-hidden="true">// </span>pass rate with 95% interval — {rows.length} runs
      </div>
      <ol className="iv-list" role="list" aria-labelledby={labelId}>
        {rows.map((r, i) => {
          const spoken =
            r.rate === null
              ? `${r.name}: no data`
              : `${r.name}: ${fmt(r.rate)} percent, ` +
                (r.lo !== null ? `interval ${fmt(r.lo)} to ${fmt(r.hi!)}` : "interval unknown");
          return (
            <li key={i} className="iv-row" role="listitem">
              <span className="viz-sr">{spoken}</span>
              <span className="iv-name" aria-hidden="true">
                {r.name}
              </span>
              <div className="iv-track" aria-hidden="true">
                {hasBand && (
                  <div className="iv-band" style={{ left: `${bandLo}%`, width: `${bandHi - bandLo}%` }} />
                )}
                {hasBase && <div className="iv-base" style={{ left: `${clamp(base!)}%` }} />}
                {r.rate === null ? (
                  <span className="iv-nodata">no data</span>
                ) : (
                  <>
                    {r.lo !== null && (
                      <div
                        className="iv-range"
                        style={{ left: `${clamp(r.lo)}%`, width: `${clamp(r.hi!) - clamp(r.lo)}%` }}
                      />
                    )}
                    <div className={"iv-pt " + r.kind} style={{ left: `${clamp(r.rate)}%` }} />
                  </>
                )}
              </div>
              <span className="iv-val" aria-hidden="true">
                {r.rate === null ? "n/a" : `${fmt(r.rate)}%`}
                {r.lo !== null && <span className="iv-ci"> [{fmt(r.lo)}, {fmt(r.hi!)}]</span>}
              </span>
            </li>
          );
        })}
      </ol>
      <div className="iv-axis" aria-hidden="true">
        {[0, 25, 50, 75, 100].map((t) => (
          <span key={t} className="iv-axis-tick" style={{ left: `${t}%` }}>
            {t}%
          </span>
        ))}
      </div>
      <div className="gc-legend" aria-hidden="true">
        <span>● point = pass rate, bar = 95% interval</span>
        {hasBase && <span>│ reference {fmt(base!)}%</span>}
        {hasBand && <span>▒ ±{fmt(band!)} points: gaps inside this band cannot be told apart</span>}
      </div>
    </div>
  );
}

export default IntervalPlot;
